/**
 * INV-3: Dock Compatibility Detection Helpers
 *
 * Centralized logic for detecting mismatches between PO receiving metadata
 * and dock capability tags. Warnings only — never blocks any action.
 */

import { aggregatePOMetadata, type POMetaFields } from './poMetadataHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DockCapabilities {
  temperatureZone?: string | null;
  handlingCapabilities?: string | null;
}

export interface CompatibilityResult {
  temperatureMismatch: boolean;
  handlingMismatch: boolean;
  compatibilityLabel: string | null; // null = no warning needed
}

// ─── Core Detection ───────────────────────────────────────────────────────────

/**
 * Detect temperature mismatch between a dock's zone and a PO's required temperature.
 * Returns false (no mismatch) if either value is null/empty or dock is "mixed".
 */
export function detectTemperatureMismatch(
  dockTempZone: string | null | undefined,
  poTemp: string | null | undefined
): boolean {
  if (!dockTempZone || !poTemp) return false;
  const dz = dockTempZone.toLowerCase().trim();
  const pt = poTemp.toLowerCase().trim();
  // "mixed" zone matches everything
  if (dz === 'mixed' || dz === 'mixed-use') return false;
  return dz !== pt;
}

/**
 * Detect handling mismatch: PO requires a handling type that dock doesn't support.
 * Returns false if either value is null/empty.
 */
export function detectHandlingMismatch(
  dockCapabilities: string | null | undefined,
  poHandlingType: string | null | undefined
): boolean {
  if (!dockCapabilities || !poHandlingType) return false;
  const caps = dockCapabilities
    .toLowerCase()
    .split(',')
    .map(c => c.trim())
    .filter(Boolean);
  if (caps.length === 0) return false;
  const required = poHandlingType.toLowerCase().trim();
  // Normalize common variants
  const normalize = (s: string) =>
    s.replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
  return !caps.some(c => normalize(c) === normalize(required));
}

// ─── Composite Detection ──────────────────────────────────────────────────────

/**
 * Evaluate full compatibility between a dock and an appointment's linked POs.
 *
 * Multi-PO safety:
 * - If POs have mixed temperature → "Compatibility varies by PO" (skip verdict)
 * - If POs have identical temp → evaluate normally
 */
export function summarizeCompatibilityStatus(
  dock: DockCapabilities,
  pos: POMetaFields[],
  opts?: { loadLinked?: boolean }
): CompatibilityResult {
  const noWarning: CompatibilityResult = {
    temperatureMismatch: false,
    handlingMismatch: false,
    compatibilityLabel: null,
  };

  if (!pos || pos.length === 0) return noWarning;

  // Check for mixed temperature across POs
  const temps = [...new Set(pos.map(p => p.requiredTemperature).filter(Boolean))];
  const handlings = [...new Set(pos.map(p => p.handlingType).filter(Boolean))];

  // Multi-PO mixed scenario — skip verdict
  if (temps.length > 1) {
    return {
      temperatureMismatch: false,
      handlingMismatch: false,
      compatibilityLabel: opts?.loadLinked
        ? 'Mixed temperature requirements'
        : 'Compatibility varies by PO',
    };
  }

  // Evaluate single temperature (or identical across POs)
  const poTemp = temps[0] || null;
  const poHandling = handlings.length === 1 ? handlings[0] : null;

  const tempMismatch = detectTemperatureMismatch(dock.temperatureZone, poTemp);
  const handMismatch = detectHandlingMismatch(dock.handlingCapabilities, poHandling);

  if (!tempMismatch && !handMismatch) return noWarning;

  let label: string;
  if (tempMismatch && handMismatch) {
    label = 'Dock incompatible';
  } else if (tempMismatch) {
    label = 'Temp mismatch';
  } else {
    label = 'Equipment mismatch';
  }

  return {
    temperatureMismatch: tempMismatch,
    handlingMismatch: handMismatch,
    compatibilityLabel: label,
  };
}

// ─── Facility-Level Detection (for approval queue) ────────────────────────────

/**
 * Find docks in the facility that match a given temperature requirement.
 * Returns matching dock IDs.
 *
 * A dock matches if:
 * - Its temperature_zone equals the required temperature (case-insensitive), OR
 * - Its temperature_zone is "mixed"/"mixed-use"
 */
export function findCompatibleDocks(
  docks: { id: string; temperatureZone?: string | null }[],
  requiredTemp: string | null | undefined
): string[] {
  if (!requiredTemp) return docks.map(d => d.id); // No requirement = all compatible
  const rt = requiredTemp.toLowerCase().trim();
  return docks.filter(d => {
    if (!d.temperatureZone) return false; // Unconfigured dock = not confirmed compatible
    const tz = d.temperatureZone.toLowerCase().trim();
    return tz === rt || tz === 'mixed' || tz === 'mixed-use';
  }).map(d => d.id);
}
